import React, { useState } from 'react';

import { c, msgid } from 'ttag';

import { ChevronDown } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

import { PermissionsQuery } from '../../graphql/__generated__/types';
import { PortalModuleRef, buildRightsGroups } from './grouping';

type Permission = NonNullable<NonNullable<PermissionsQuery['permissions']>[number]>;

interface Props {
    referential: Permission[];
    modules: PortalModuleRef[];
    granted: Set<string>;
}

const RightsMatrix = ({ referential, modules, granted }: Props) => {
    const groups = buildRightsGroups(referential, modules);

    const labelByCode = new Map(referential.map((permission) => [permission.code ?? '', permission.label ?? '']));

    // Store the OPEN set, not the closed one: an empty Set means all closed, which is the
    // wanted default. Groups arrive by query, so the first render has none to seed from.
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const toggle = (key: string) =>
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });

    const total = referential.filter((permission) => granted.has(permission.code ?? '')).length;

    return (
        <div className="flex flex-col gap-2">
            {groups.map((group) => {
                const grantedHere = group.permissions.filter((permission) => granted.has(permission.code ?? ''));
                const isOpen = expanded.has(group.key);

                return (
                    <section
                        key={group.key}
                        className={clsx(
                            'overflow-hidden rounded-md border',
                            group.anomaly ? 'border-warning-500' : 'border-border-norm'
                        )}
                    >
                        <h3>
                            <button
                                type="button"
                                onClick={() => toggle(group.key)}
                                aria-expanded={isOpen}
                                className="flex w-full items-center gap-2.5 bg-background-weak px-3 py-2.5 text-left"
                            >
                                <ChevronDown
                                    size={12}
                                    className={clsx(
                                        'shrink-0 text-text-500 transition-transform',
                                        !isOpen && '-rotate-90'
                                    )}
                                />
                                <span className="text-sm font-semibold text-text-900">{group.label}</span>
                                {group.delivered === false && (
                                    <span className="rounded-full border border-border-norm bg-background-norm px-2 py-0.5 text-[11px] font-medium text-text-600">
                                        {c('Rights matrix').t`Screen not delivered yet`}
                                    </span>
                                )}
                                {group.anomaly && (
                                    <span className="rounded-full border border-warning-500 px-2 py-0.5 text-[11px] font-medium text-warning-700">
                                        {c('Rights matrix').t`Unexpected`}
                                    </span>
                                )}
                                <span className="ml-auto shrink-0 rounded-full border border-border-norm bg-background-norm px-2 py-0.5 font-mono text-xs text-text-600">
                                    {grantedHere.length}/{group.permissions.length}
                                </span>
                            </button>
                        </h3>

                        {isOpen && (
                            <ul className="border-t border-border-norm px-3 py-1.5">
                                {group.permissions.length === 0 && (
                                    <li className="py-1.5 text-sm text-text-500">
                                        {c('Rights matrix').t`No right attached to this module yet.`}
                                    </li>
                                )}
                                {group.permissions.map((permission) => {
                                    const isGranted = granted.has(permission.code ?? '');
                                    // Self-comparison guards the sentinel the database stores for
                                    // a right that depends on nothing.
                                    const requiredCode =
                                        permission.requiresPermissionCode &&
                                        permission.requiresPermissionCode !== permission.code
                                            ? permission.requiresPermissionCode
                                            : null;
                                    const requiredLabel = requiredCode
                                        ? labelByCode.get(requiredCode) || requiredCode
                                        : null;

                                    return (
                                        <li key={permission.code} className="flex items-start gap-2.5 py-1.5">
                                            <input
                                                type="checkbox"
                                                checked={isGranted}
                                                disabled
                                                readOnly
                                                aria-label={permission.label ?? permission.code ?? ''}
                                                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-admin-accent"
                                            />
                                            <span className="min-w-0">
                                                <span
                                                    className={clsx(
                                                        'block text-sm',
                                                        isGranted ? 'text-text-900' : 'text-text-500'
                                                    )}
                                                >
                                                    {permission.label}
                                                </span>
                                                <span className="block font-mono text-[11px] text-text-500">
                                                    {permission.code}
                                                </span>
                                                {permission.description && (
                                                    <span className="block text-xs text-text-500">
                                                        {permission.description}
                                                    </span>
                                                )}
                                                {requiredLabel && (
                                                    <span className="block text-xs text-text-500">
                                                        {c('Rights matrix').t`Requires: ${requiredLabel}`}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                );
            })}

            <p className="mt-1 self-start rounded-full border border-border-norm bg-background-weak px-2.5 py-1 font-mono text-xs text-text-600">
                {total === 0
                    ? c('Rights matrix').t`No right granted`
                    : c('Rights matrix').ngettext(msgid`${total} right granted`, `${total} rights granted`, total)}
            </p>
        </div>
    );
};

export default RightsMatrix;
